/**
 * Shared parent handbook PDF links for packet flows (14th Ave, Rockland, 55th, Woodmere).
 * Prefer token-scoped API PDF when a site has a dedicated handbook form slot.
 */

/** Dispatched after markHandbookOpened so form_19 gating updates without remount. */
export const HANDBOOK_OPENED_EVENT = 'packet-handbook-opened';

export function handbookOpenedStorageKey(token) {
  return `packet_handbook_opened_${token || ''}`;
}

/** Survives reload within the same browser tab/session (keyed by packet token). */
export function readHandbookOpened(token) {
  if (!token || typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(handbookOpenedStorageKey(token)) === '1';
  } catch {
    return false;
  }
}

/** Persist unlock + notify listeners (FormFields form_19 gate). */
export function markHandbookOpened(token) {
  if (!token) return;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(handbookOpenedStorageKey(token), '1');
    }
  } catch {
    /* ignore quota / private mode */
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(HANDBOOK_OPENED_EVENT, { detail: { token } }));
  }
}

const HANDBOOK_FORM_BY_SITE = {
  woodmere: 'wm_15',
  '55th-street': 'bs_15',
};

const STATIC_HANDBOOK_BY_SITE = {
  rockland: '/packet/rockland/parent-handbook-2026-2027.pdf',
  // 14th Ave has no standalone handbook PDF — parent info is on fa_01/fa_02/fa_15.
  '55th-street': '/packet/55th-street/parent-handbook-2026-2027.pdf',
  woodmere: '/packet/woodmere/parent-handbook-2026-2027.pdf',
};

export function handbookFormIdForSite(siteSlug) {
  return HANDBOOK_FORM_BY_SITE[siteSlug] || null;
}

/**
 * Resolve href for "View parent handbook (PDF)".
 *
 * Uses the API-provided handbook_url (backend checks file existence on disk).
 * Falls back to the static path table only when the API didn't provide a URL.
 */
export function parentHandbookPdfHref({ siteSlug, token, apiBaseUrl, handbookUrl }) {
  if (handbookUrl) return handbookUrl;
  const staticHref = STATIC_HANDBOOK_BY_SITE[siteSlug];
  if (staticHref) return staticHref;
  const handbookFormId = handbookFormIdForSite(siteSlug);
  if (handbookFormId && token && apiBaseUrl) {
    return `${apiBaseUrl}/packet/${encodeURIComponent(token)}/${encodeURIComponent(handbookFormId)}/pdf/`;
  }
  return null;
}

/** Open handbook PDF in a new tab without navigating the current page. */
export function openParentHandbookPdf(href) {
  if (!href) return;
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}
